import React, { useState, useEffect, useMemo } from 'react';
import {
  Briefcase,
  Star,
  DollarSign,
  Clock,
  CheckCircle2,
  AlertCircle,
  Send,
  MessageSquare,
  MapPin,
  ShieldCheck,
  ChevronRight,
  Eye,
  Bell,
  CreditCard,
  Sparkles,
  Check,
  Zap,
  Mail,
  Smartphone,
  Camera,
  User,
  Settings,
  LogOut,
  ArrowRight,
  FileText,
  RefreshCw,
  Search,
  Filter,
  SlidersHorizontal,
  ExternalLink,
  Phone,
  AlertTriangle,
  Building2,
  HelpCircle,
  PlusCircle,
  TrendingUp,
  Compass,
} from 'lucide-react';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { useApp } from '../../context/AppContext';
import { VerificationBadge } from '../../components/common/VerificationBadge';
import { CategoryIcon } from '../../components/common/CategoryIcon';
import { SubmitQuoteModal } from './SubmitQuoteModal';
import { PushNotificationSettingsCard } from '../../components/notifications/PushNotificationSettingsCard';
import { JobDetailsModal } from '../../components/jobs/JobDetailsModal';
import { RealTimeJobStatusTracker } from '../../components/jobs/RealTimeJobStatusTracker';
import { CompletionPhotoUploadModal } from '../../components/professional/CompletionPhotoUploadModal';
import { KYCVerificationCenter } from '../../components/professional/KYCVerificationCenter';
import { ProfessionalProfileEditSection } from '../../components/professional/ProfessionalProfileEditSection';
import { EarningsSummaryCard } from '../../components/professional/EarningsSummaryCard';
import {
  Job,
  ProfessionalEarningsSummary,
  ProfessionalPayout,
  ProfessionalBankAccount,
  NigerianBankOption,
} from '../../types';
import {
  canProfessionalViewAvailableJob,
  getAssignedProfessionalId,
  getJobCanonicalTradeSlug,
  getProfessionalAllowedCategorySlugs,
} from '../../lib/matchingEngine';
import {
  supabase,
  fetchProfessionalEarnings,
  fetchProfessionalReviewsApi,
  requestProfessionalPayoutApi,
  fetchNigerianBanksApi,
  resolveProfessionalBankAccountApi,
  saveProfessionalBankAccountApi,
  reconcileProfessionalPayoutApi,
  fetchProfessionalNotificationPreferences,
  updateProfessionalNotificationPreferences,
  getSupabaseJobById,
  cleanContactPhone,
} from '../../lib/supabaseService';

export type ProfessionalDashboardTab =
  | 'overview'
  | 'available_jobs'
  | 'accepted_jobs'
  | 'active_jobs'
  | 'awaiting_confirmation'
  | 'completed_jobs'
  | 'earnings'
  | 'payouts'
  | 'messages'
  | 'profile'
  | 'verification'
  | 'notifications'
  | 'help';

export const ProfessionalDashboard: React.FC = () => {
  const {
    currentUser,
    jobs,
    quotes,
    profiles,
    notifications,
    markNotificationAsRead,
    navigateTo,
    logoutUser,
    updateProfessionalProfile,
    viewParams,
    refreshJobsAndQuotes,
  } = useApp();

  const [activeTab, setActiveTab] = useState<ProfessionalDashboardTab>('overview');
  const [selectedJobForQuote, setSelectedJobForQuote] = useState<Job | null>(null);
  const [selectedJobForDetails, setSelectedJobForDetails] = useState<Job | null>(null);
  const [completionModalJob, setCompletionModalJob] = useState<Job | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean>(currentUser?.isAvailable ?? true);
  const [updatingAvailability, setUpdatingAvailability] = useState<boolean>(false);
  const [availabilityNotice, setAvailabilityNotice] = useState<string | null>(null);

  // Search & Filter state for Available Jobs
  const [jobSearchQuery, setJobSearchQuery] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<string>('all');
  const [selectedBudgetFilter, setSelectedBudgetFilter] = useState<string>('all');
  const [selectedLgaFilter, setSelectedLgaFilter] = useState<string>('all');
  const [selectedUrgencyFilter, setSelectedUrgencyFilter] = useState<string>('all');

  // Earnings & Payouts Real Database State
  const [earningsSummary, setEarningsSummary] = useState<ProfessionalEarningsSummary>({
    totalJobsCompleted: 0,
    totalGrossJobValue: 0,
    totalJobMatchCommission: 0,
    totalProfessionalEarnings: 0,
    pendingEarnings: 0,
    availableEarnings: 0,
    paidOutEarnings: 0,
    successfulTransactionsCount: 0,
    recentTransactions: [],
    payoutHistory: [],
  });
  const [loadingEarnings, setLoadingEarnings] = useState(false);

  // Payout & Verified Paystack Bank Account State
  const [nigerianBanks, setNigerianBanks] = useState<NigerianBankOption[]>([]);
  const [verifiedBankAccount, setVerifiedBankAccount] = useState<ProfessionalBankAccount | null>(null);
  const [isEditingBank, setIsEditingBank] = useState<boolean>(false);
  const [payoutAmount, setPayoutAmount] = useState<string>('');
  const [payoutBankCode, setPayoutBankCode] = useState<string>('');
  const [payoutBankName, setPayoutBankName] = useState<string>('');
  const [payoutAccountNumber, setPayoutAccountNumber] = useState<string>('');
  const [payoutAccountName, setPayoutAccountName] = useState<string>('');
  const [isResolvingBankAccount, setIsResolvingBankAccount] = useState<boolean>(false);
  const [isSavingBankAccount, setIsSavingBankAccount] = useState<boolean>(false);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);
  const [reconcilingReference, setReconcilingReference] = useState<string | null>(null);
  const [payoutIdempotencyKey, setPayoutIdempotencyKey] = useState<string>('');
  const [payoutFeedback, setPayoutFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Logout Confirmation State
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Synchronize URL viewParams
  useEffect(() => {
    if (viewParams?.jobId) {
      const targetJob = jobs.find((j) => j.id === viewParams.jobId);
      if (targetJob) {
        setSelectedJobForDetails(targetJob);
      } else {
        getSupabaseJobById(viewParams.jobId).then((fetchedJob) => {
          if (fetchedJob) setSelectedJobForDetails(fetchedJob);
        });
      }
    }
    if (viewParams?.adminTab) {
      setActiveTab(viewParams.adminTab as ProfessionalDashboardTab);
    }
  }, [viewParams?.jobId, viewParams?.adminTab, jobs]);

  const [liveRatingStats, setLiveRatingStats] = useState<{
    averageRating: number;
    totalReviews: number;
  }>({
    averageRating: Number(currentUser?.rating || 0),
    totalReviews: Number(currentUser?.reviewCount || 0),
  });

  // Load Authoritative Real Earnings, Bank Account & Reviews from Backend API
  const loadEarnings = async () => {
    if (!currentUser?.id) return;
    setLoadingEarnings(true);
    try {
      const [res, revRes, banksRes] = await Promise.all([
        fetchProfessionalEarnings(currentUser.id),
        fetchProfessionalReviewsApi(currentUser.id),
        nigerianBanks.length === 0 ? fetchNigerianBanksApi() : Promise.resolve({ success: true, banks: nigerianBanks }),
      ]);
      if (banksRes.banks?.length && nigerianBanks.length === 0) {
        setNigerianBanks(banksRes.banks);
      }
      if (res.success && res.earnings) {
        setEarningsSummary(res.earnings);
        if (res.earnings.bankAccount) {
          setVerifiedBankAccount(res.earnings.bankAccount);
          setPayoutBankCode((prev) => prev || res.earnings.bankAccount?.bankCode || '');
          setPayoutBankName((prev) => prev || res.earnings.bankAccount?.bankName || '');
          setPayoutAccountNumber((prev) => prev || res.earnings.bankAccount?.accountNumber || '');
          setPayoutAccountName((prev) => prev || res.earnings.bankAccount?.accountName || '');
        }
      }
      if (revRes.success && revRes.totalReviews > 0) {
        setLiveRatingStats({
          averageRating: Number(revRes.averageRating || currentUser.rating || 5.0),
          totalReviews: Number(revRes.totalReviews || 0),
        });
      }
    } catch (err) {
      console.warn('Load earnings error:', err);
    } finally {
      setLoadingEarnings(false);
    }
  };

  useEffect(() => {
    loadEarnings();
    if (currentUser?.id) {
      setPayoutIdempotencyKey(`wd_${currentUser.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
    }
  }, [currentUser?.id]);

  // Toggle Work Availability
  const handleToggleAvailability = async () => {
    const nextState = !isAvailable;
    setIsAvailable(nextState);
    setUpdatingAvailability(true);
    setAvailabilityNotice(null);
    try {
      await updateProfessionalProfile({ isAvailable: nextState });
      setAvailabilityNotice(
        nextState
          ? 'Status Updated: You are now AVAILABLE for new job requests in your trade and location!'
          : 'Status Updated: You are now marked OFF-DUTY / BUSY. Your profile is excluded from new search results.'
      );
      setTimeout(() => setAvailabilityNotice(null), 4500);
    } catch (err) {
      console.warn('Update availability notice:', err);
      setIsAvailable(!nextState);
    } finally {
      setUpdatingAvailability(false);
    }
  };

  // Auto-resolve Nigerian NUBAN Account Name via Paystack
  const handleAutoResolveBankAccount = async (targetBankCode: string, targetAccountNumber: string) => {
    if (!currentUser?.id) return;
    const cleanNum = targetAccountNumber.replace(/\D/g, '');
    if (!targetBankCode || cleanNum.length !== 10) {
      setPayoutAccountName('');
      return;
    }

    setIsResolvingBankAccount(true);
    setPayoutAccountName('');
    setPayoutFeedback(null);
    try {
      const res = await resolveProfessionalBankAccountApi({
        professionalId: currentUser.id,
        bankCode: targetBankCode,
        accountNumber: cleanNum,
      });
      if (res.success && res.accountName) {
        setPayoutAccountName(res.accountName);
      } else {
        setPayoutAccountName('');
        if (res.error) {
          setPayoutFeedback({ type: 'error', text: res.error });
        }
      }
    } catch (err: any) {
      setPayoutAccountName('');
      setPayoutFeedback({ type: 'error', text: err.message || 'Could not resolve bank account.' });
    } finally {
      setIsResolvingBankAccount(false);
    }
  };

  // Save & Verify Bank Account + Create Paystack Transfer Recipient
  const handleSaveVerifiedBankAccount = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!currentUser?.id) return;

    const cleanAcct = payoutAccountNumber.replace(/\D/g, '');
    if (!payoutBankCode || cleanAcct.length !== 10) {
      setPayoutFeedback({
        type: 'error',
        text: 'Please select a Nigerian bank and enter a valid 10-digit NUBAN account number.',
      });
      return;
    }

    setIsSavingBankAccount(true);
    setPayoutFeedback(null);
    try {
      const res = await saveProfessionalBankAccountApi({
        professionalId: currentUser.id,
        bankCode: payoutBankCode,
        bankName: payoutBankName,
        accountNumber: cleanAcct,
      });

      if (res.success && res.bankAccount) {
        setVerifiedBankAccount(res.bankAccount);
        setPayoutBankCode(res.bankAccount.bankCode);
        setPayoutBankName(res.bankAccount.bankName);
        setPayoutAccountNumber(res.bankAccount.accountNumber);
        setPayoutAccountName(res.bankAccount.accountName);
        setIsEditingBank(false);
        setPayoutFeedback({
          type: 'success',
          text:
            res.message ||
            `Verified ${res.bankAccount.accountName} (${res.bankAccount.bankName}) for Paystack withdrawals.`,
        });
        loadEarnings();
      } else {
        setPayoutFeedback({
          type: 'error',
          text: res.error || 'Failed to verify bank account with Paystack.',
        });
      }
    } catch (err: any) {
      setPayoutFeedback({
        type: 'error',
        text: err.message || 'Network error verifying bank account.',
      });
    } finally {
      setIsSavingBankAccount(false);
    }
  };

  // Reconcile Transfer Status with Paystack
  const handleReconcilePayout = async (reference: string) => {
    if (!currentUser?.id || !reference) return;
    setReconcilingReference(reference);
    setPayoutFeedback(null);
    try {
      const res = await reconcileProfessionalPayoutApi({
        professionalId: currentUser.id,
        reference,
      });
      if (res.success) {
        setPayoutFeedback({
          type: 'success',
          text: `Transfer ${reference} verified with Paystack: status is ${(res.reconciledStatus || res.payout?.status || 'processing').toUpperCase()}.`,
        });
        loadEarnings();
      } else {
        setPayoutFeedback({
          type: 'error',
          text: res.error || 'Could not reconcile transfer status with Paystack.',
        });
      }
    } catch (err: any) {
      setPayoutFeedback({
        type: 'error',
        text: err.message || 'Error reconciling transfer status.',
      });
    } finally {
      setReconcilingReference(null);
    }
  };

  // Handle Automated Paystack Withdrawal Submission
  const handleRequestPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.id || isRequestingPayout) return;

    const amountNum = parseFloat(payoutAmount);
    if (isNaN(amountNum) || amountNum < 1000) {
      setPayoutFeedback({ type: 'error', text: 'Minimum withdrawal amount is ₦1,000.' });
      return;
    }

    if (amountNum > earningsSummary.availableEarnings) {
      setPayoutFeedback({
        type: 'error',
        text: `Requested amount exceeds available balance of ₦${earningsSummary.availableEarnings.toLocaleString()}.`,
      });
      return;
    }

    const cleanAcct = (payoutAccountNumber || verifiedBankAccount?.accountNumber || '').replace(/\D/g, '');
    const targetBankCode = payoutBankCode || verifiedBankAccount?.bankCode || '';
    const targetBankName = payoutBankName || verifiedBankAccount?.bankName || '';
    const targetAcctName = payoutAccountName || verifiedBankAccount?.accountName || '';

    if (cleanAcct.length !== 10 || (!targetBankCode && !targetBankName)) {
      setPayoutFeedback({
        type: 'error',
        text: 'Please verify your 10-digit Nigerian bank account before withdrawing.',
      });
      return;
    }

    setIsRequestingPayout(true);
    setPayoutFeedback(null);

    try {
      const idemKey =
        payoutIdempotencyKey ||
        `wd_${currentUser.id}_${Math.round(amountNum * 100)}_${Date.now()}`;

      const res = await requestProfessionalPayoutApi({
        professionalId: currentUser.id,
        amount: amountNum,
        bankCode: targetBankCode,
        bankName: targetBankName.trim(),
        accountNumber: cleanAcct,
        accountName: targetAcctName.trim(),
        idempotencyKey: idemKey,
      });

      if (res.success) {
        setPayoutFeedback({
          type: 'success',
          text:
            res.message ||
            'Withdrawal initiated via Paystack Transfers. Your balance has been reserved while Paystack completes the transfer.',
        });
        setPayoutAmount('');
        setPayoutIdempotencyKey(`wd_${currentUser.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
        loadEarnings();
      } else {
        setPayoutIdempotencyKey(`wd_${currentUser.id}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
        setPayoutFeedback({ type: 'error', text: res.error || 'Failed to initiate withdrawal.' });
      }
    } catch (err: any) {
      setPayoutFeedback({ type: 'error', text: err.message || 'Network error processing withdrawal.' });
    } finally {
      setIsRequestingPayout(false);
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await logoutUser();
      navigateTo('home');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setIsLoggingOut(false);
      setShowLogoutConfirm(false);
    }
  };

  if (!currentUser) return null;

  // Real Database Metrics & Jobs Calculation
  const allowedProCategorySlugs = useMemo(
    () => getProfessionalAllowedCategorySlugs(currentUser),
    [currentUser]
  );

  const proQualifiedCategories = useMemo(
    () => SERVICE_CATEGORIES.filter((cat) => allowedProCategorySlugs.has(cat.slug)),
    [allowedProCategorySlugs]
  );

  // Direct Job Requests specifically targeting this professional while open
  const directRequestedJobs = useMemo(
    () =>
      jobs.filter(
        (j) =>
          j.status === 'open' &&
          !j.cancelled_at &&
          !j.expired_at &&
          getAssignedProfessionalId(j) === currentUser.id
      ),
    [jobs, currentUser.id]
  );

  // 1. Matched / Available Jobs (Strictly restricted to this professional's trade or direct assignment)
  const matchedJobs = useMemo(
    () =>
      jobs
        .filter((j) => canProfessionalViewAvailableJob(j, currentUser))
        .sort((a, b) => {
          const aDirect = getAssignedProfessionalId(a) === currentUser.id ? 1 : 0;
          const bDirect = getAssignedProfessionalId(b) === currentUser.id ? 1 : 0;
          return bDirect - aDirect;
        }),
    [jobs, currentUser]
  );

  // Distinct LGAs present across this professional's eligible open jobs for the dropdown filter
  const availableJobLgas = useMemo(() => {
    const lgaSet = new Set<string>();
    matchedJobs.forEach((j) => {
      if (j.lga && j.lga.trim()) {
        lgaSet.add(j.lga.trim());
      }
    });
    return Array.from(lgaSet).sort();
  }, [matchedJobs]);

  // Filtered available jobs by category, budget range, and LGA location (strictly scoped to this artisan's trade)
  const filteredAvailableJobs = useMemo(() => {
    return matchedJobs.filter((job) => {
      // 1. Text Search Query
      const q = jobSearchQuery.trim().toLowerCase();
      if (q) {
        const catName = job.categoryName || (job as any).category_name || '';
        const matchesSearch =
          (job.title || '').toLowerCase().includes(q) ||
          (job.description || '').toLowerCase().includes(q) ||
          (job.lga || '').toLowerCase().includes(q) ||
          ((job.cityArea || (job as any).city_area) && String(job.cityArea || (job as any).city_area).toLowerCase().includes(q)) ||
          catName.toLowerCase().includes(q);
        if (!matchesSearch) return false;
      }

      // 2. Trade Category Filter (within the professional's own registered trades)
      if (selectedCategoryFilter !== 'all') {
        const targetCat = selectedCategoryFilter.toLowerCase();
        const canonicalSlug = getJobCanonicalTradeSlug(job);
        const jobCat = (job.categorySlug || (job as any).category_slug || '').toLowerCase();
        if (canonicalSlug !== targetCat && jobCat !== targetCat) {
          return false;
        }
      }

      // 3. Budget Range Filter
      const budget = Number(job.budgetEstimate ?? (job as any).budget_estimate ?? 0);
      if (selectedBudgetFilter !== 'all') {
        if (selectedBudgetFilter === 'under_20k' && budget >= 20000) return false;
        if (selectedBudgetFilter === '20k_50k' && (budget < 20000 || budget > 50000)) return false;
        if (selectedBudgetFilter === '50k_100k' && (budget < 50000 || budget > 100000)) return false;
        if (selectedBudgetFilter === '100k_250k' && (budget < 100000 || budget > 250000)) return false;
        if (selectedBudgetFilter === 'above_250k' && budget <= 250000) return false;
      }

      // 4. LGA Location Filter
      if (selectedLgaFilter !== 'all') {
        const targetLga = selectedLgaFilter.toLowerCase();
        const jobLga = (job.lga || '').toLowerCase();
        const jobArea = (job.cityArea || '').toLowerCase();
        if (jobLga !== targetLga && !jobArea.includes(targetLga)) return false;
      }

      // 5. Urgency Filter
      if (selectedUrgencyFilter !== 'all' && job.urgency !== selectedUrgencyFilter) return false;

      return true;
    });
  }, [matchedJobs, jobSearchQuery, selectedCategoryFilter, selectedBudgetFilter, selectedLgaFilter, selectedUrgencyFilter]);

  // 2. My Quotes
  const myQuotes = quotes.filter((q) => q.professionalId === currentUser.id);
  const acceptedQuotes = myQuotes.filter((q) => q.status === 'accepted');

  // Helper to verify if professional is assigned to or hired for this job
  const isJobAssignedToPro = (j: Job) => {
    const assignedId = getAssignedProfessionalId(j);
    if (assignedId) {
      return assignedId === currentUser.id;
    }
    return acceptedQuotes.some((q) => q.jobId === j.id);
  };

  // Authoritative helper to verify if a job is fully completed and confirmed by customer / paid
  const isJobTrulyCompleted = (j: Job) => {
    const s = String(j.status || '').toLowerCase();
    const cs = String(j.completionStatus || (j as any).completion_status || '').toLowerCase();
    const ps = String((j as any).payment_status || (j as any).paymentStatus || '').toLowerCase();
    return Boolean(
      s === 'completed' ||
      cs === 'confirmed' ||
      ps === 'released' ||
      j.payment_released ||
      (j as any).paymentReleased ||
      j.confirmed_by_customer ||
      (j as any).confirmedByCustomer ||
      (j as any).customer_confirmed_at ||
      j.completed_by_customer_at
    );
  };

  // Authoritative helper to verify if work was marked complete by professional and is awaiting customer confirmation
  const isJobAwaitingConfirmation = (j: Job) => {
    if (isJobTrulyCompleted(j) || j.status === 'cancelled' || j.status === 'expired' || j.cancelled_at || j.expired_at) return false;
    const s = String(j.status || '').toLowerCase();
    const cs = String(j.completionStatus || (j as any).completion_status || '').toLowerCase();
    return Boolean(
      s === 'completed_by_professional' ||
      s === 'professional_completed' ||
      cs === 'completed_by_professional' ||
      cs === 'awaiting_customer_confirmation' ||
      j.completed_by_professional ||
      j.completed_by_pro_at ||
      j.completionPhotoUrl
    );
  };

  // Authoritative helper to verify if an assigned job is still awaiting customer Paystack escrow payment
  const isJobAwaitingEscrowPayment = (j: Job) => {
    if (isJobTrulyCompleted(j) || isJobAwaitingConfirmation(j) || j.status === 'cancelled' || j.status === 'expired' || j.cancelled_at || j.expired_at) {
      return false;
    }
    const rawPay = String(
      j.paymentStatus ||
        (j as any).payment_status ||
        (j.aiScopeEstimate as any)?.lifecycle_meta?.payment_status ||
        ''
    ).toLowerCase();
    const isFunded = rawPay === 'held_in_escrow' || rawPay === 'paid' || rawPay === 'released';
    return !isFunded && (j.status === 'assigned' || rawPay === 'awaiting_payment' || rawPay === 'pending');
  };

  // Accepted Jobs Awaiting Customer Escrow Payment (Work is strictly locked until payment is verified)
  const acceptedAwaitingPaymentJobs = jobs.filter(
    (j) => isJobAssignedToPro(j) && isJobAwaitingEscrowPayment(j)
  );

  // 3. Active Jobs (Strictly escrow-funded jobs where THIS professional is currently executing work)
  const activeJobs = jobs.filter(
    (j) =>
      isJobAssignedToPro(j) &&
      !isJobTrulyCompleted(j) &&
      !isJobAwaitingConfirmation(j) &&
      !isJobAwaitingEscrowPayment(j) &&
      !j.cancelled_at &&
      !j.expired_at &&
      (j.status === 'assigned' || j.status === 'in_progress')
  );

  // Combined Accepted Jobs (both awaiting escrow payment and funded active jobs)
  const acceptedJobs = [...acceptedAwaitingPaymentJobs, ...activeJobs];

  const [acceptingJobId, setAcceptingJobId] = useState<string | null>(null);
  const handleAcceptMatchingJob = async (job: Job) => {
    if (!currentUser) return;
    setAcceptingJobId(job.id);
    setAvailabilityNotice(null);
    try {
      const res = await fetch(`/api/jobs/${encodeURIComponent(job.id)}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          professionalId: currentUser.id,
          professionalName: currentUser.fullName,
          amount: job.budgetEstimate || 15000,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Could not accept job.');
      }
      setAvailabilityNotice(
        `You accepted "${job.title}"! The customer has been sent a Telegram & in-app link to complete Paystack escrow payment. Do NOT start work until payment is confirmed.`
      );
      if (refreshJobsAndQuotes) {
        await refreshJobsAndQuotes();
      }
      setActiveTab('accepted_jobs');
    } catch (err: any) {
      setAvailabilityNotice(err.message || 'Failed to accept job.');
    } finally {
      setAcceptingJobId(null);
    }
  };

  // 4. Awaiting Customer Confirmation Jobs (Work marked complete with photo, awaiting customer release — NEVER includes completed jobs)
  const awaitingConfirmationJobs = jobs.filter(
    (j) =>
      isJobAssignedToPro(j) &&
      isJobAwaitingConfirmation(j)
  );

  // 5. Completed Jobs (All jobs where work was confirmed and/or payment released, deduplicated by job id)
  const completedJobs = useMemo(() => {
    const seen = new Set<string>();
    return jobs.filter((j) => {
      if (!j?.id || seen.has(j.id)) return false;
      if (isJobAssignedToPro(j) && isJobTrulyCompleted(j)) {
        seen.add(j.id);
        return true;
      }
      return false;
    });
  }, [jobs, currentUser?.id, quotes]);

  // Data-Driven Summary Metrics: 'Jobs Completed this Month', 'Total Earnings', 'Average Rating'
  const jobsCompletedThisMonth = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    return completedJobs.filter((j) => {
      const rawDate =
        j.completed_at ||
        j.completedByCustomerAt ||
        (j as any).payment_released_at ||
        (j as any).customer_confirmed_at ||
        j.updatedAt ||
        j.createdAt;
      if (!rawDate) return false;
      const d = new Date(rawDate);
      return !isNaN(d.getTime()) && d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    }).length;
  }, [completedJobs]);

  const computedTotalEarnings = useMemo(() => {
    if (earningsSummary.totalProfessionalEarnings > 0) {
      return earningsSummary.totalProfessionalEarnings;
    }
    const seenJobIds = new Set<string>();
    return completedJobs.reduce((sum, j) => {
      if (!j?.id || seenJobIds.has(j.id)) return sum;
      seenJobIds.add(j.id);
      const explicitProEarnings = Number(
        (j as any).professional_earnings ||
        (j as any).aiScopeEstimate?.lifecycle_meta?.professional_earnings ||
        0
      );
      if (explicitProEarnings > 0) return sum + explicitProEarnings;
      const acceptedQ = quotes.find((q) => q.jobId === j.id && q.status === 'accepted');
      const gross = Number(acceptedQ?.totalAmount || j.budgetEstimate || 0);
      return sum + Math.round(gross * 0.9);
    }, 0);
  }, [earningsSummary.totalProfessionalEarnings, completedJobs, quotes]);

  const computedAverageRating = useMemo(() => {
    const jobRatings = completedJobs
      .map((j) =>
        Number(
          (j as any).customer_rating ||
          (j as any).aiScopeEstimate?.lifecycle_meta?.customer_rating ||
          0
        )
      )
      .filter((r) => r > 0);

    if (liveRatingStats.totalReviews > 0 && liveRatingStats.averageRating > 0) {
      return {
        rating: Number(liveRatingStats.averageRating.toFixed(1)),
        count: Math.max(liveRatingStats.totalReviews, jobRatings.length),
      };
    }
    if (jobRatings.length > 0) {
      const avg = jobRatings.reduce((a, b) => a + b, 0) / jobRatings.length;
      return {
        rating: Number(avg.toFixed(1)),
        count: jobRatings.length,
      };
    }
    return {
      rating: Number((currentUser.rating || 5.0).toFixed(1)),
      count: Number(currentUser.reviewCount || completedJobs.length || 0),
    };
  }, [completedJobs, liveRatingStats, currentUser.rating, currentUser.reviewCount]);

  // Real Notifications (Strictly scoped to this professional)
  const proNotifications = notifications.filter(
    (n) => n.userId === currentUser.id
  );
  const unreadNotifsCount = proNotifications.filter((n) => !n.read).length;

  const getGoogleMapsUrl = (job: any) => {
    if (job.locationLatitude && job.locationLongitude) {
      return `https://www.google.com/maps/search/?api=1&query=${job.locationLatitude},${job.locationLongitude}`;
    }
    const cleanAddress = job.address || job.formattedAddress || '';
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${cleanAddress}${job.cityArea ? ', ' + job.cityArea : ''}, ${job.lga || ''}, ${job.state || ''}, Nigeria`
    )}`;
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        
        {/* Header Profile & Quick Status Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <img
              src={currentUser.avatarUrl || 'https://images.unsplash.com/photo-1540569014015-19a7be504e3a?w=200&auto=format&fit=crop&q=80'}
              alt={currentUser.fullName || 'Professional'}
              className="w-16 h-16 rounded-2xl object-cover ring-2 ring-emerald-100 shrink-0"
            />
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900">{currentUser.fullName}</h1>
                <VerificationBadge status={currentUser.verificationStatus || 'pending'} />
                <span className="px-2.5 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-50 text-emerald-800 border border-emerald-200/60 uppercase">
                  {currentUser.primaryCategory || 'Artisan Pro'}
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 text-slate-400" />
                  {currentUser.lga || 'Ikeja'}, {currentUser.state || 'Lagos'}
                </span>
                <span>•</span>
                <span>★ {currentUser.rating?.toFixed(1) || '5.0'} ({currentUser.reviewCount || 0} reviews)</span>
                <span>•</span>
                <span>{currentUser.yearsExperience || 3}+ years exp.</span>
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* Availability Toggle Switch */}
            <div className="flex items-center gap-3 p-2 px-3.5 rounded-2xl bg-slate-50 border border-slate-200/80 shadow-2xs">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${isAvailable ? 'bg-emerald-500 animate-pulse ring-4 ring-emerald-100' : 'bg-slate-400'}`}></span>
                <div className="text-left">
                  <span className="text-[10px] font-bold text-slate-400 block uppercase tracking-wider">Availability</span>
                  <span className={`text-xs font-black ${isAvailable ? 'text-emerald-700' : 'text-slate-600'}`}>
                    {isAvailable ? 'Available for Jobs' : 'Off-Duty / Busy'}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleToggleAvailability}
                disabled={updatingAvailability}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden ${
                  isAvailable ? 'bg-emerald-600' : 'bg-slate-300'
                }`}
                aria-label="Set Availability Toggle"
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                    isAvailable ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <button
              type="button"
              onClick={() => setActiveTab('earnings')}
              className="px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-xs transition-all flex items-center gap-1.5 cursor-pointer min-h-[44px]"
            >
              <CreditCard className="w-3.5 h-3.5 text-emerald-400" />
              <span>Balance: ₦{earningsSummary.availableEarnings.toLocaleString()}</span>
            </button>
          </div>
        </div>

        {/* Availability Notification Toast Alert */}
        {availabilityNotice && (
          <div className="p-4 rounded-2xl bg-emerald-900 text-white border border-emerald-700 text-xs font-bold shadow-md flex items-center justify-between gap-3 animate-in fade-in">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping"></span>
              <span>{availabilityNotice}</span>
            </div>
            <button onClick={() => setAvailabilityNotice(null)} className="text-white hover:text-emerald-200 text-sm font-mono cursor-pointer">✕</button>
          </div>
        )}

        {/* Action Alert Banner for Awaiting Customer Confirmation */}
        {awaitingConfirmationJobs.length > 0 && (
          <div className="bg-gradient-to-r from-purple-900 to-indigo-950 text-white rounded-3xl p-6 shadow-md border border-purple-800 space-y-3">
            <div className="flex items-center gap-2 text-purple-300">
              <Clock className="w-4 h-4 text-amber-400 animate-spin" />
              <span className="text-xs font-black uppercase tracking-wider">
                {awaitingConfirmationJobs.length} Job(s) Awaiting Customer Completion Confirmation
              </span>
            </div>
            <p className="text-xs text-purple-200">
              You marked these job(s) complete with photo evidence. Escrow funds (90% net earnings) will be released to your available balance as soon as the customer confirms.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <button
                onClick={() => setActiveTab('awaiting_confirmation')}
                className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white font-bold text-xs cursor-pointer"
              >
                View Awaiting Jobs ({awaitingConfirmationJobs.length})
              </button>
            </div>
          </div>
        )}

        {/* Professional Quick Access Hub: Matching Jobs, Accepted Jobs, Active Jobs, Earnings, Payouts, Messages, Notifications */}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2.5">
          <button
            type="button"
            onClick={() => setActiveTab('available_jobs')}
            className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer ${
              activeTab === 'available_jobs'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <Sparkles className={`w-4 h-4 ${activeTab === 'available_jobs' ? 'text-white' : 'text-amber-500'}`} />
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-900">
                {matchedJobs.length}
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">Matching Jobs</span>
              <span className={`text-[10px] block ${activeTab === 'available_jobs' ? 'text-emerald-100' : 'text-slate-500'}`}>
                Accept or quote
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('accepted_jobs')}
            className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer ${
              activeTab === 'accepted_jobs'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <Clock className={`w-4 h-4 ${activeTab === 'accepted_jobs' ? 'text-white' : 'text-amber-600'}`} />
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-900">
                {acceptedJobs.length}
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">Accepted Jobs</span>
              <span className={`text-[10px] block ${activeTab === 'accepted_jobs' ? 'text-emerald-100' : 'text-slate-500'}`}>
                {acceptedAwaitingPaymentJobs.length > 0
                  ? `${acceptedAwaitingPaymentJobs.length} awaiting payment`
                  : 'Payment status'}
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('active_jobs')}
            className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer ${
              activeTab === 'active_jobs'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <Briefcase className={`w-4 h-4 ${activeTab === 'active_jobs' ? 'text-white' : 'text-blue-600'}`} />
              <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-blue-100 text-blue-900">
                {activeJobs.length}
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">Active Jobs</span>
              <span className={`text-[10px] block ${activeTab === 'active_jobs' ? 'text-emerald-100' : 'text-slate-500'}`}>
                Escrow funded
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('earnings')}
            className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer ${
              activeTab === 'earnings'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <DollarSign className={`w-4 h-4 ${activeTab === 'earnings' ? 'text-white' : 'text-emerald-600'}`} />
              <span className={`text-[10px] font-bold ${activeTab === 'earnings' ? 'text-emerald-100' : 'text-emerald-700'}`}>
                90% Share
              </span>
            </div>
            <div>
              <span className="font-extrabold text-xs block">Earnings</span>
              <span className={`text-[10px] block truncate ${activeTab === 'earnings' ? 'text-emerald-100' : 'text-slate-500'}`}>
                ₦{earningsSummary.availableEarnings.toLocaleString()} avail.
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('payouts')}
            className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer ${
              activeTab === 'payouts'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <CreditCard className={`w-4 h-4 ${activeTab === 'payouts' ? 'text-white' : 'text-emerald-600'}`} />
            </div>
            <div>
              <span className="font-extrabold text-xs block">Payouts</span>
              <span className={`text-[10px] block ${activeTab === 'payouts' ? 'text-emerald-100' : 'text-slate-500'}`}>
                Bank withdrawal
              </span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => navigateTo('messages')}
            className="p-3 rounded-2xl border border-slate-200/80 bg-white hover:border-emerald-400 text-slate-900 text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <MessageSquare className="w-4 h-4 text-purple-600" />
            </div>
            <div>
              <span className="font-extrabold text-xs block">Messages</span>
              <span className="text-[10px] text-slate-500 block">Customer chat</span>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('notifications')}
            className={`p-3 rounded-2xl border text-left transition-all flex flex-col justify-between gap-1.5 cursor-pointer ${
              activeTab === 'notifications'
                ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                : 'bg-white hover:border-emerald-400 text-slate-900 border-slate-200/80'
            }`}
          >
            <div className="flex items-center justify-between">
              <Bell className={`w-4 h-4 ${activeTab === 'notifications' ? 'text-white' : 'text-slate-700'}`} />
              {unreadNotifsCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-black bg-rose-500 text-white">
                  {unreadNotifsCount}
                </span>
              )}
            </div>
            <div>
              <span className="font-extrabold text-xs block">Notifications</span>
              <span className={`text-[10px] block ${activeTab === 'notifications' ? 'text-emerald-100' : 'text-slate-500'}`}>
                Alerts & updates
              </span>
            </div>
          </button>
        </div>

        {/* Warning Banner: Accepted Jobs Awaiting Customer Escrow Payment (Work Locked) */}
        {acceptedAwaitingPaymentJobs.length > 0 && (
          <div className="bg-amber-50 border-2 border-amber-300 rounded-3xl p-5 shadow-xs space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-amber-950">
                <Clock className="w-4 h-4 text-amber-700 shrink-0" />
                <span className="text-xs font-black uppercase tracking-wider">
                  {acceptedAwaitingPaymentJobs.length} Accepted Job(s) Awaiting Customer Escrow Payment — Do Not Start Work Yet
                </span>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('accepted_jobs')}
                className="px-3.5 py-1.5 rounded-xl bg-amber-900 hover:bg-amber-950 text-white font-bold text-xs cursor-pointer"
              >
                View Accepted Jobs ({acceptedJobs.length})
              </button>
            </div>
            <p className="text-xs text-amber-900">
              The customer has received a Telegram & JobMatch NG link to complete Paystack escrow payment. Wait until you receive confirmation that payment is secured in escrow before travelling or starting work.
            </p>
          </div>
        )}

        {/* Dashboard Navigation Tabs */}
        <div className="flex items-center gap-1.5 p-1.5 bg-white rounded-2xl border border-slate-200/80 shadow-2xs overflow-x-auto scrollbar-none">
          <button
            type="button"
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'overview'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Briefcase className="w-3.5 h-3.5" />
            <span>Overview</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('available_jobs')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'available_jobs'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Matching Jobs</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'available_jobs' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {matchedJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('accepted_jobs')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'accepted_jobs'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Accepted Jobs</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'accepted_jobs' ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-900 font-bold'}`}>
              {acceptedJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('active_jobs')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'active_jobs'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Active Jobs</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'active_jobs' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {activeJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('earnings')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'earnings'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <DollarSign className="w-3.5 h-3.5" />
            <span>Earnings</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('payouts')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'payouts'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <CreditCard className="w-3.5 h-3.5" />
            <span>Payouts</span>
          </button>
          <button
            type="button"
            onClick={() => navigateTo('messages')}
            className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer"
          >
            <MessageSquare className="w-3.5 h-3.5" />
            <span>Messages</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('notifications')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'notifications'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <Bell className="w-3.5 h-3.5" />
            <span>Notifications</span>
            {unreadNotifsCount > 0 && <span className="w-2 h-2 rounded-full bg-rose-500"></span>}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('awaiting_confirmation')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'awaiting_confirmation'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Awaiting Confirmation</span>
            {awaitingConfirmationJobs.length > 0 && (
              <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-amber-500 text-slate-950 font-black">
                {awaitingConfirmationJobs.length}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('completed_jobs')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'completed_jobs'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <span>Completed</span>
            <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${activeTab === 'completed_jobs' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {completedJobs.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('profile')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'profile'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            <span>Profile</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('verification')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'verification'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            <span>Identity & KYC</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('help')}
            className={`px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-2 cursor-pointer ${
              activeTab === 'help'
                ? 'bg-emerald-600 text-white shadow-xs'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
            }`}
          >
            <HelpCircle className="w-3.5 h-3.5" />
            <span>Help & Support</span>
          </button>
          <button
            type="button"
            onClick={() => setShowLogoutConfirm(true)}
            className="px-4 py-2 rounded-xl text-xs font-bold text-rose-600 hover:bg-rose-50 transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ml-auto"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span>Logout</span>
          </button>
        </div>

        {/* TAB 1: OVERVIEW */}
        {activeTab === 'overview' && (
          <div className="space-y-6 animate-fade-in">
            {/* Data-Driven Executive Performance Summary Card */}
            <div
              data-testid="pro-executive-summary-card"
              className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200/90 shadow-xs space-y-5"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-50 border border-emerald-200/70 flex items-center justify-center text-emerald-600 shrink-0">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 block">
                      Real-Time Artisan Telemetry
                    </span>
                    <h2 className="text-base sm:text-lg font-black text-slate-900">
                      Performance & Earnings Summary
                    </h2>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-[11px] font-extrabold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    {new Date().toLocaleString('en-NG', { month: 'long', year: 'numeric' })}
                  </span>
                  <button
                    type="button"
                    onClick={loadEarnings}
                    disabled={loadingEarnings}
                    className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors cursor-pointer disabled:opacity-50"
                    title="Refresh Metrics"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loadingEarnings ? 'animate-spin text-emerald-600' : ''}`} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Metric 1: Jobs Completed this Month */}
                <button
                  type="button"
                  onClick={() => setActiveTab('completed_jobs')}
                  className="p-5 rounded-2xl bg-slate-50/80 hover:bg-emerald-50/40 border border-slate-200/80 hover:border-emerald-300 transition-all text-left flex flex-col justify-between gap-3 group cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-extrabold text-slate-600">
                      Jobs Completed this Month
                    </span>
                    <div className="w-9 h-9 rounded-xl bg-emerald-100/80 text-emerald-700 flex items-center justify-center shrink-0">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900 tabular-nums">
                      {jobsCompletedThisMonth}
                    </span>
                    <span className="text-xs font-bold text-emerald-700">
                      / {completedJobs.length} all-time
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/70 flex items-center justify-between text-[11px] text-slate-500">
                    <span>Verified customer confirmations</span>
                    <span className="font-bold text-emerald-700 group-hover:underline flex items-center gap-0.5">
                      History <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>

                {/* Metric 2: Total Earnings */}
                <button
                  type="button"
                  onClick={() => setActiveTab('earnings')}
                  className="p-5 rounded-2xl bg-slate-50/80 hover:bg-emerald-50/40 border border-slate-200/80 hover:border-emerald-300 transition-all text-left flex flex-col justify-between gap-3 group cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-extrabold text-slate-600">
                      Total Earnings
                    </span>
                    <div className="w-9 h-9 rounded-xl bg-emerald-100/80 text-emerald-700 flex items-center justify-center shrink-0">
                      <DollarSign className="w-4 h-4" />
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    <span className="text-3xl sm:text-4xl font-black text-emerald-700 tabular-nums block">
                      ₦{computedTotalEarnings.toLocaleString()}
                    </span>
                    <span className="text-[11px] font-semibold text-slate-500 block">
                      90% net artisan share • ₦{earningsSummary.availableEarnings.toLocaleString()} withdrawable
                    </span>
                  </div>
                  <div className="pt-2 border-t border-slate-200/70 flex items-center justify-between text-[11px] text-slate-500">
                    <span>Escrow-protected settlements</span>
                    <span className="font-bold text-emerald-700 group-hover:underline flex items-center gap-0.5">
                      Ledger <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>

                {/* Metric 3: Average Rating */}
                <button
                  type="button"
                  onClick={() => setActiveTab('completed_jobs')}
                  className="p-5 rounded-2xl bg-slate-50/80 hover:bg-amber-50/40 border border-slate-200/80 hover:border-amber-300 transition-all text-left flex flex-col justify-between gap-3 group cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-extrabold text-slate-600">
                      Average Rating
                    </span>
                    <div className="w-9 h-9 rounded-xl bg-amber-100/80 text-amber-600 flex items-center justify-center shrink-0">
                      <Star className="w-4 h-4 fill-amber-500 text-amber-500" />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900 tabular-nums">
                      {computedAverageRating.rating.toFixed(1)}
                    </span>
                    <div className="space-y-1">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            className={`w-3.5 h-3.5 ${
                              star <= Math.round(computedAverageRating.rating)
                                ? 'text-amber-400 fill-amber-400'
                                : 'text-slate-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-[11px] font-bold text-slate-500 block">
                        {computedAverageRating.count} verified review{computedAverageRating.count === 1 ? '' : 's'}
                      </span>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-slate-200/70 flex items-center justify-between text-[11px] text-slate-500">
                    <span>Customer satisfaction score</span>
                    <span className="font-bold text-amber-700 group-hover:underline flex items-center gap-0.5">
                      Reviews <ChevronRight className="w-3 h-3" />
                    </span>
                  </div>
                </button>
              </div>
            </div>

            {/* Financial Summary Component */}
            <EarningsSummaryCard
              professionalId={currentUser.id}
              summary={earningsSummary}
              onRefresh={loadEarnings}
              onRequestPayoutTab={() => setActiveTab('payouts')}
            />

            {/* Interactive 4 Command Stat Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <button
                type="button"
                onClick={() => setActiveTab('payouts')}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-emerald-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-emerald-600 text-xs font-bold">
                  <span>Available Balance</span>
                  <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center">
                    <DollarSign className="w-4 h-4 text-emerald-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-slate-900 tabular-nums">
                  ₦{earningsSummary.availableEarnings.toLocaleString()}
                </p>
                <span className="text-[11px] font-extrabold text-emerald-700 group-hover:underline flex items-center gap-1 pt-0.5">
                  <span>Request Bank Payout</span>
                  <ArrowRight className="w-3 h-3" />
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('active_jobs')}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-blue-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-blue-600 text-xs font-bold">
                  <span>Active Jobs</span>
                  <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                    <Briefcase className="w-4 h-4 text-blue-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-blue-600 tabular-nums">{activeJobs.length}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span>In progress / Escrow secured</span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('awaiting_confirmation')}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-purple-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-purple-600 text-xs font-bold">
                  <span>Awaiting Confirmation</span>
                  <div className="w-8 h-8 rounded-xl bg-purple-50 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-purple-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-purple-600 tabular-nums">{awaitingConfirmationJobs.length}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span>Photo proof submitted</span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('available_jobs')}
                className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs hover:border-amber-300 hover:shadow-md transition-all text-left space-y-1.5 group cursor-pointer"
              >
                <div className="flex items-center justify-between text-amber-600 text-xs font-bold">
                  <span>Available Matches</span>
                  <div className="w-8 h-8 rounded-xl bg-amber-50 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-black text-amber-600 tabular-nums">{matchedJobs.length}</p>
                <p className="text-[11px] text-slate-500 flex items-center gap-1">
                  <span>
                    {directRequestedJobs.length > 0
                      ? `${directRequestedJobs.length} Direct Request(s) for you!`
                      : 'Open in your trade & area'}
                  </span>
                  <ChevronRight className="w-3 h-3" />
                </p>
              </button>
            </div>

            {/* Interactive 4-Stage Artisan Workflow Pipeline Bar */}
            <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] font-black uppercase tracking-wider text-emerald-700 block">
                    Artisan Execution Pipeline
                  </span>
                  <h2 className="text-sm sm:text-base font-black text-slate-900">
                    Click Any Stage to Jump Directly to Your Jobs
                  </h2>
                </div>
                <span className="text-xs text-slate-500">
                  Completed jobs automatically exit Active Jobs and settle into Completed History
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <button
                  type="button"
                  onClick={() => setActiveTab('available_jobs')}
                  className="p-3.5 rounded-2xl border border-amber-200/80 bg-amber-50/50 hover:bg-amber-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-amber-700 block">Stage 1 • Matches</span>
                    <p className="text-xs font-black text-slate-900">Submit Quotes</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-amber-500 text-white font-black text-xs tabular-nums">
                    {matchedJobs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('active_jobs')}
                  className="p-3.5 rounded-2xl border border-blue-200/80 bg-blue-50/50 hover:bg-blue-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-blue-700 block">Stage 2 • Active</span>
                    <p className="text-xs font-black text-slate-900">Execute & Upload Photo</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-blue-600 text-white font-black text-xs tabular-nums">
                    {activeJobs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('awaiting_confirmation')}
                  className="p-3.5 rounded-2xl border border-purple-200/80 bg-purple-50/50 hover:bg-purple-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-purple-700 block">Stage 3 • Pending</span>
                    <p className="text-xs font-black text-slate-900">Customer Inspection</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-purple-600 text-white font-black text-xs tabular-nums">
                    {awaitingConfirmationJobs.length}
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => setActiveTab('completed_jobs')}
                  className="p-3.5 rounded-2xl border border-emerald-200/80 bg-emerald-50/50 hover:bg-emerald-50 text-left transition-all cursor-pointer flex items-center justify-between gap-3"
                >
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-extrabold uppercase text-emerald-700 block">Stage 4 • Settled</span>
                    <p className="text-xs font-black text-slate-900">Completed & Paid (90%)</p>
                  </div>
                  <span className="px-2.5 py-1 rounded-xl bg-emerald-600 text-white font-black text-xs tabular-nums">
                    {completedJobs.length}
                  </span>
                </button>
              </div>
            </div>

            {/* Direct Customer Requests Priority Banner */}
            {directRequestedJobs.length > 0 && (
              <div className="bg-gradient-to-r from-emerald-950 via-emerald-900 to-teal-950 text-white p-6 rounded-3xl shadow-md border border-emerald-700/60 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-emerald-300">
                    <Sparkles className="w-4 h-4 text-amber-400" />
                    <span className="text-xs font-black uppercase tracking-wider">
                      ★ {directRequestedJobs.length} Direct Customer Job Request{directRequestedJobs.length === 1 ? '' : 's'} Assigned Specifically to You
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('available_jobs')}
                    className="text-xs font-bold text-emerald-200 hover:text-white flex items-center gap-1 cursor-pointer"
                  >
                    <span>View All</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {directRequestedJobs.map((dJob) => (
                    <div
                      key={dJob.id}
                      className="p-4 rounded-2xl bg-white/10 border border-white/15 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                    >
                      <div className="space-y-0.5">
                        <h4 className="text-sm font-black text-white">{dJob.title}</h4>
                        <p className="text-xs text-emerald-200">
                          {dJob.lga}, {dJob.state} • Budget: ₦{(dJob.budgetEstimate || 0).toLocaleString()}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedJobForQuote(dJob)}
                        className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-sm cursor-pointer shrink-0"
                      >
                        Send Quote Now
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Jobs in Progress with Upload Completion Photo trigger */}
            {activeJobs.length > 0 && (
              <div className="bg-gradient-to-r from-emerald-900 to-slate-900 text-white p-6 rounded-3xl shadow-md space-y-4 border border-emerald-700/40">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center text-emerald-400">
                      <Camera className="w-5 h-5" />
                    </div>
                    <div>
                      <span className="text-[10px] font-extrabold tracking-wider uppercase text-emerald-300 block">
                        Work Completion Evidence
                      </span>
                      <h2 className="text-base sm:text-lg font-black text-white">
                        Active Jobs In Progress ({activeJobs.length})
                      </h2>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setActiveTab('active_jobs')}
                    className="text-xs font-bold text-emerald-300 hover:text-white flex items-center gap-1 cursor-pointer"
                  >
                    <span>View In Active Jobs Tab</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-1">
                  {activeJobs.map((job) => (
                    <div
                      key={job.id}
                      className="bg-white/10 backdrop-blur-xs p-4 rounded-2xl border border-white/10 space-y-3 flex flex-col justify-between"
                    >
                      <div className="space-y-1">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-black text-white truncate">{job.title}</h4>
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold bg-blue-500/30 text-blue-200 border border-blue-400/30 shrink-0">
                            In Progress
                          </span>
                        </div>
                        <p className="text-xs text-slate-300 flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-emerald-400 shrink-0" />
                          <span className="truncate">{job.lga}, {job.state}</span>
                        </p>
                        <p className="text-xs text-slate-300">
                          Customer: <strong className="text-white">{job.customerName || 'Customer'}</strong>
                        </p>
                      </div>

                      <div className="pt-2 border-t border-white/10 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedJobForDetails(job)}
                          className="px-3 py-1.5 rounded-xl bg-white/15 hover:bg-white/25 text-white font-bold text-xs cursor-pointer transition-colors"
                        >
                          Details
                        </button>
                        <button
                          type="button"
                          onClick={() => setCompletionModalJob(job)}
                          className="px-4 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md cursor-pointer flex items-center gap-1.5 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <Camera className="w-3.5 h-3.5" />
                          <span>Upload Completion Photo</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions & Recent Available Jobs */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-base sm:text-lg font-black text-slate-900">New Available Job Matches</h2>
                  <button
                    type="button"
                    onClick={() => setActiveTab('available_jobs')}
                    className="text-xs font-bold text-emerald-700 hover:text-emerald-800 flex items-center gap-1 cursor-pointer"
                  >
                    <span>View All ({matchedJobs.length})</span>
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                </div>

                {matchedJobs.length === 0 ? (
                  <div className="bg-white rounded-3xl p-8 sm:p-12 text-center border border-slate-200/80 space-y-3">
                    <Briefcase className="w-8 h-8 text-slate-400 mx-auto" />
                    <h3 className="text-base font-black text-slate-900">No open job requests in your trade right now</h3>
                    <p className="text-xs text-slate-500 max-w-sm mx-auto">
                      Make sure your profile has complete trade categories and service areas to receive automatic job matches.
                    </p>
                    <button
                      onClick={() => setActiveTab('profile')}
                      className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs cursor-pointer"
                    >
                      Update Service Areas
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {matchedJobs.slice(0, 4).map((job) => (
                      <div
                        key={job.id}
                        className="bg-white rounded-3xl p-5 border border-slate-200/80 shadow-xs hover:border-slate-300 transition-all space-y-3"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                              <CategoryIcon slug={job.categorySlug} className="w-5 h-5" />
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-1.5">
                                <h3 className="text-sm sm:text-base font-black text-slate-900">{job.title}</h3>
                                {getAssignedProfessionalId(job) === currentUser.id && (
                                  <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-600 text-white shadow-2xs">
                                    ★ Direct Request for You
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-slate-500 flex items-center gap-1.5">
                                <span>{job.lga}, {job.state}</span>
                                <span>•</span>
                                <span>Est. Budget: ₦{(job.budgetEstimate || 0).toLocaleString()}</span>
                              </p>
                            </div>
                          </div>
                          {job.urgency === 'emergency' && (
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-200 uppercase w-fit">
                              Emergency
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-slate-600 line-clamp-2">{job.description}</p>

                        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
                          <button
                            type="button"
                            onClick={() => setSelectedJobForDetails(job)}
                            className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
                          >
                            Details
                          </button>
                          {quotes.some((q) => q.jobId === job.id && q.professionalId === currentUser?.id) ? (
                            <span className="px-3.5 py-1.5 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 font-extrabold text-xs flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                              <span>Quote Sent</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSelectedJobForQuote(job)}
                              className="px-4 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-xs cursor-pointer flex items-center gap-1"
                            >
                              <span>Submit Quote</span>
                              <ArrowRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right Col: Performance & Fast Withdrawal */}
              <div className="space-y-4">
                <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4">
                  <h3 className="text-sm font-black text-slate-900">JobMatch NG 90/10 Model</h3>
                  <div className="p-4 bg-emerald-50/80 border border-emerald-200/80 rounded-2xl space-y-2 text-xs text-emerald-950">
                    <p className="font-extrabold">Zero Monthly Subscription Fees!</p>
                    <p className="text-emerald-900">
                      You keep <strong>90% of every job payment</strong>. JobMatch NG retains only a 10% platform fee upon successful customer confirmation.
                    </p>
                  </div>
                  <div className="space-y-2 pt-1 text-xs">
                    <div className="flex justify-between text-slate-600">
                      <span>Total Jobs Resolved:</span>
                      <strong className="text-slate-900">{completedJobs.length}</strong>
                    </div>
                    <div className="flex justify-between text-slate-600">
                      <span>Lifetime Earnings:</span>
                      <strong className="text-emerald-700">₦{earningsSummary.totalProfessionalEarnings.toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-3xl p-6 shadow-md space-y-3">
                  <h3 className="text-sm font-black text-white">Instant Bank Payout</h3>
                  <p className="text-xs text-slate-300">
                    Transfer your available funds directly into your Nigerian bank account anytime.
                  </p>
                  <button
                    onClick={() => setActiveTab('payouts')}
                    className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs shadow-md transition-all cursor-pointer"
                  >
                    Withdraw ₦{earningsSummary.availableEarnings.toLocaleString()}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: AVAILABLE JOBS (Search & Quote) */}
        {activeTab === 'available_jobs' && (
          <div className="space-y-6 animate-fade-in">
            {/* Search and Multi-Criteria Filtering System */}
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2 text-slate-900 font-black text-sm">
                  <Filter className="w-4 h-4 text-emerald-600" />
                  <span>Available Jobs Filter</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-600">
                    Matches: <strong className="text-emerald-700 font-extrabold">{filteredAvailableJobs.length}</strong>
                  </span>
                  {(jobSearchQuery || selectedCategoryFilter !== 'all' || selectedBudgetFilter !== 'all' || selectedLgaFilter !== 'all' || selectedUrgencyFilter !== 'all') && (
                    <button
                      type="button"
                      onClick={() => {
                        setJobSearchQuery('');
                        setSelectedCategoryFilter('all');
                        setSelectedBudgetFilter('all');
                        setSelectedLgaFilter('all');
                        setSelectedUrgencyFilter('all');
                      }}
                      className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] cursor-pointer"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                {/* 1. Keyword Search */}
                <div className="relative">
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                    Search Fault or Keyword
                  </label>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
                    <input
                      type="text"
                      value={jobSearchQuery}
                      onChange={(e) => setJobSearchQuery(e.target.value)}
                      placeholder="Search title, fault, or area..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-xs text-slate-800 focus:outline-hidden focus:border-emerald-500 font-medium"
                    />
                  </div>
                </div>

                {/* 2. Trade Category Filter (Strictly Scoped to Artisan's Registered Trades) */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                    Your Qualified Trade(s)
                  </label>
                  <select
                    value={selectedCategoryFilter}
                    onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 bg-white focus:outline-hidden focus:border-emerald-500"
                  >
                    <option value="all">
                      My Registered Trades ({proQualifiedCategories.length || 1})
                    </option>
                    {proQualifiedCategories.map((cat) => (
                      <option key={cat.slug} value={cat.slug}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* 3. Budget Range Filter */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                    Target Budget Range
                  </label>
                  <select
                    value={selectedBudgetFilter}
                    onChange={(e) => setSelectedBudgetFilter(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 bg-white focus:outline-hidden focus:border-emerald-500"
                  >
                    <option value="all">All Budget Ranges</option>
                    <option value="under_20k">Under ₦20,000</option>
                    <option value="20k_50k">₦20,000 – ₦50,000</option>
                    <option value="50k_100k">₦50,000 – ₦100,000</option>
                    <option value="100k_250k">₦100,000 – ₦250,000</option>
                    <option value="above_250k">Above ₦250,000</option>
                  </select>
                </div>

                {/* 4. Location (LGA) Filter */}
                <div>
                  <label className="block text-[10px] font-bold uppercase text-slate-400 mb-1">
                    Location (LGA)
                  </label>
                  <select
                    value={selectedLgaFilter}
                    onChange={(e) => setSelectedLgaFilter(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-xs font-bold text-slate-800 bg-white focus:outline-hidden focus:border-emerald-500"
                  >
                    <option value="all">All LGAs ({availableJobLgas.length})</option>
                    {availableJobLgas.map((lga) => (
                      <option key={lga} value={lga}>
                        {lga}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* List */}
            {filteredAvailableJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <Briefcase className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No matching jobs found</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Try clearing your search query or broadening your filters.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredAvailableJobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs hover:border-slate-300 transition-all space-y-4"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center shrink-0">
                          <CategoryIcon slug={job.categorySlug} className="w-6 h-6" />
                        </div>
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base sm:text-lg font-black text-slate-900">{job.title}</h3>
                            {getAssignedProfessionalId(job) === currentUser.id && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-emerald-600 text-white shadow-2xs">
                                ★ Direct Request for You
                              </span>
                            )}
                            {job.urgency === 'emergency' && (
                              <span className="px-2.5 py-0.5 rounded-full text-[10px] font-black bg-rose-50 text-rose-700 border border-rose-200">
                                Emergency
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 flex flex-wrap items-center gap-2">
                            <span>{job.categoryName}</span>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3.5 h-3.5 text-slate-400" />
                              {job.lga}, {job.state}
                            </span>
                            <span>•</span>
                            <span>Posted {new Date(job.createdAt).toLocaleDateString()}</span>
                          </p>
                        </div>
                      </div>

                      <div className="sm:text-right shrink-0">
                        <span className="text-[10px] uppercase font-bold text-slate-400 block">Est. Budget</span>
                        <span className="text-lg font-black text-slate-900">₦{(job.budgetEstimate || 0).toLocaleString()}</span>
                      </div>
                    </div>

                    <p className="text-xs text-slate-600 leading-relaxed">{job.description}</p>

                    <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 text-xs">
                      <span className="text-slate-500">Customer: <strong>{job.customerName || 'Verified Homeowner'}</strong></span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSelectedJobForDetails(job)}
                          className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
                        >
                          View Details & Media
                        </button>
                        {quotes.some((q) => q.jobId === job.id && q.professionalId === currentUser?.id) ? (
                          <span className="px-4 py-2 rounded-xl bg-emerald-50 text-emerald-800 border border-emerald-200 font-extrabold text-xs flex items-center gap-1.5">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span>Quote Sent</span>
                          </span>
                        ) : (
                          <>
                            <button
                              type="button"
                              disabled={acceptingJobId === job.id}
                              onClick={() => handleAcceptMatchingJob(job)}
                              className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs shadow-xs cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
                            >
                              <span>{acceptingJobId === job.id ? 'Accepting...' : 'Accept Job'}</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setSelectedJobForQuote(job)}
                              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-xs cursor-pointer flex items-center gap-1.5"
                            >
                              <span>Submit Quote</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 2B: ACCEPTED JOBS (Shows jobs awaiting customer escrow payment AND funded jobs) */}
        {activeTab === 'accepted_jobs' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900">Accepted Jobs</h2>
                <p className="text-xs text-slate-500">
                  Jobs you have accepted or where your quote was selected. Do not start work until customer Paystack escrow payment is verified.
                </p>
              </div>
              <span className="px-3.5 py-1.5 rounded-2xl bg-amber-50 text-amber-900 border border-amber-200 text-xs font-extrabold w-fit">
                {acceptedJobs.length} Accepted ({acceptedAwaitingPaymentJobs.length} Awaiting Payment)
              </span>
            </div>

            {acceptedJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <Briefcase className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No accepted jobs right now</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Browse matching jobs in your trade and click Accept Job or Submit Quote.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveTab('available_jobs')}
                  className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold cursor-pointer"
                >
                  Browse Matching Jobs ({matchedJobs.length})
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {acceptedJobs.map((job) => {
                  const awaitingPay = isJobAwaitingEscrowPayment(job);
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const grossValue = Number(acceptedQuote?.totalAmount || job.grossAmount || job.budgetEstimate || 0);
                  const netPayout = Math.round(grossValue * 0.9);

                  return (
                    <div
                      key={`acc-${job.id}`}
                      className={`bg-white rounded-3xl p-6 border shadow-xs space-y-4 ${
                        awaitingPay ? 'border-amber-300 bg-amber-50/20' : 'border-emerald-200'
                      }`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-black text-slate-900">{job.title}</h3>
                            {awaitingPay ? (
                              <span className="px-3 py-0.5 rounded-full text-[11px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300">
                                ⏳ Awaiting Customer Escrow Payment — Work Locked
                              </span>
                            ) : (
                              <span className="px-3 py-0.5 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-900 border border-emerald-300">
                                ✅ Escrow Confirmed — Ready for Work
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-slate-500">
                            {job.lga}, {job.state} • Agreed Value: <strong>₦{grossValue.toLocaleString()}</strong> (Your 90% Share: <strong>₦{netPayout.toLocaleString()}</strong>)
                          </p>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setSelectedJobForDetails(job)}
                            className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
                          >
                            Job Details
                          </button>
                          <button
                            type="button"
                            onClick={() => navigateTo('messages', { jobId: job.id })}
                            className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs cursor-pointer flex items-center gap-1.5"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>Messages</span>
                          </button>
                          {!awaitingPay && (
                            <button
                              type="button"
                              onClick={() => setCompletionModalJob(job)}
                              className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs cursor-pointer flex items-center gap-1.5"
                            >
                              <Camera className="w-3.5 h-3.5" />
                              <span>Upload Completion Photo</span>
                            </button>
                          )}
                        </div>
                      </div>

                      {awaitingPay && (
                        <div className="p-3 rounded-xl bg-amber-100/80 border border-amber-300 text-xs text-amber-950 font-semibold">
                          ⚠️ Do not travel or start work yet. The customer has been sent a secure Paystack payment link via Telegram and JobMatch NG. You will be notified as soon as payment is verified.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 3: ACTIVE JOBS (In Progress — Strictly excludes completed & awaiting confirmation jobs) */}
        {activeTab === 'active_jobs' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900">Active Jobs In Progress</h2>
                <p className="text-xs text-slate-500">
                  Jobs with 100% funded escrow currently assigned to you. Upload a completion photo when finished to trigger customer payout release.
                </p>
              </div>
              <span className="px-3.5 py-1.5 rounded-2xl bg-blue-50 text-blue-800 border border-blue-200 text-xs font-extrabold w-fit">
                {activeJobs.length} Active
              </span>
            </div>

            {activeJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <Briefcase className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No active jobs currently in progress</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  {awaitingConfirmationJobs.length > 0
                    ? `You have ${awaitingConfirmationJobs.length} job(s) awaiting customer confirmation.`
                    : completedJobs.length > 0
                    ? `You have ${completedJobs.length} completed job(s) in your Completed tab.`
                    : 'When a customer accepts your quote and funds escrow, your job will appear here ready for execution.'}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                  <button
                    onClick={() => setActiveTab('available_jobs')}
                    className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold cursor-pointer"
                  >
                    Browse Available Jobs ({matchedJobs.length})
                  </button>
                  {awaitingConfirmationJobs.length > 0 && (
                    <button
                      onClick={() => setActiveTab('awaiting_confirmation')}
                      className="px-4 py-2 rounded-xl bg-purple-600 text-white text-xs font-bold cursor-pointer"
                    >
                      View Awaiting Confirmation ({awaitingConfirmationJobs.length})
                    </button>
                  )}
                  {completedJobs.length > 0 && (
                    <button
                      onClick={() => setActiveTab('completed_jobs')}
                      className="px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold cursor-pointer"
                    >
                      View Completed Jobs ({completedJobs.length})
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {activeJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const grossValue = acceptedQuote?.totalAmount || job.budgetEstimate || 0;
                  const netPayout = Math.round(grossValue * 0.9);

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-black text-slate-900">{job.title}</h3>
                            <span className="px-3 py-0.5 rounded-full text-[11px] font-extrabold bg-blue-50 text-blue-800 border border-blue-200">
                              Job In Progress
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">{job.categoryName} • {job.lga}, {job.state}</p>
                        </div>
                        <div className="sm:text-right">
                          <span className="text-[10px] font-bold uppercase text-slate-400 block">Your 90% Net Payout (In Escrow)</span>
                          <span className="text-base font-black text-emerald-700 tabular-nums">₦{netPayout.toLocaleString()}</span>
                        </div>
                      </div>

                      <p className="text-xs text-slate-600">{job.description}</p>

                      {/* Live 4-Stage Progress Tracker */}
                      <RealTimeJobStatusTracker job={job} quotes={jobQuotes} compact={true} onRefresh={refreshJobsAndQuotes} />

                      {/* Exact Job Site Location Block */}
                      <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                          <div className="flex items-start gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                              <MapPin className="w-4 h-4" />
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                                Exact Job Site Address
                              </span>
                              <p className="text-xs font-black text-slate-900 leading-snug">
                                {job.address || job.formattedAddress || `${job.cityArea ? job.cityArea + ', ' : ''}${job.lga}, ${job.state}`}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {job.cityArea ? `${job.cityArea}, ` : ''}{job.lga} LGA, {job.state} State
                              </p>
                            </div>
                          </div>

                          <a
                            href={getGoogleMapsUrl(job)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-2xs shrink-0 transition-colors w-fit"
                          >
                            <Compass className="w-3.5 h-3.5" />
                            <span>Open in Google Maps</span>
                          </a>
                        </div>

                        {(job.locationLatitude && job.locationLongitude || job.locationLandmark) && (
                          <div className="pt-2 border-t border-slate-200/60 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                            {job.locationLatitude && job.locationLongitude && (
                              <div className="flex items-center gap-1 text-slate-500">
                                <span>GPS Pin:</span>
                                <span className="font-mono text-slate-700 font-semibold">
                                  {job.locationLatitude.toFixed(5)}, {job.locationLongitude.toFixed(5)}
                                </span>
                              </div>
                            )}
                            {job.locationLandmark && (
                              <div className="sm:col-span-2 text-slate-700 bg-amber-50 p-2 rounded-xl border border-amber-200/60">
                                <span className="font-bold text-amber-900">Landmark & Directions:</span>{' '}
                                <span>{job.locationLandmark}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {(() => {
                        const custProf = profiles.find((p) => p.id === job.customerId);
                        const resolvedCustomerPhone =
                          cleanContactPhone(job.customerPhone) ||
                          cleanContactPhone(custProf?.phone) ||
                          cleanContactPhone((job.aiScopeEstimate as any)?.customer_meta?.customerPhone);
                        const resolvedCustomerName =
                          job.customerName && job.customerName !== 'Customer'
                            ? job.customerName
                            : custProf?.fullName || job.customerName || 'Customer';

                        return (
                          <div className="p-4 bg-emerald-50/60 border border-emerald-200/60 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="space-y-0.5">
                              <span className="text-[11px] font-bold text-emerald-900 block">Customer Information:</span>
                              <p className="text-xs text-slate-700">
                                <strong>{resolvedCustomerName}</strong> •{' '}
                                {resolvedCustomerPhone || 'Phone not provided'}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              {resolvedCustomerPhone && (
                                <a
                                  href={`tel:${resolvedCustomerPhone}`}
                                  className="px-3.5 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-bold flex items-center gap-1"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                  <span>Call</span>
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => navigateTo('messages', { jobId: job.id, proId: currentUser.id })}
                                className="px-3.5 py-1.5 rounded-xl bg-slate-900 text-white text-xs font-bold flex items-center gap-1 cursor-pointer"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                <span>Chat</span>
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => setSelectedJobForDetails(job)}
                          className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 font-bold text-xs cursor-pointer"
                        >
                          Job Details & AI Scope
                        </button>
                        <button
                          type="button"
                          onClick={() => setCompletionModalJob(job)}
                          className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-md cursor-pointer flex items-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
                          title="Upload Completion Photo"
                        >
                          <Camera className="w-4 h-4" />
                          <span>Upload Completion Photo</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 4: AWAITING CUSTOMER CONFIRMATION */}
        {activeTab === 'awaiting_confirmation' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900">Awaiting Customer Physical Inspection & Escrow Release</h2>
                <p className="text-xs text-slate-500">
                  You have submitted completion photo proof for these jobs. Once the customer confirms, 90% net earnings credit your available balance.
                </p>
              </div>
              <span className="px-3.5 py-1.5 rounded-2xl bg-purple-50 text-purple-900 border border-purple-200 text-xs font-extrabold w-fit">
                {awaitingConfirmationJobs.length} Pending Release
              </span>
            </div>

            {awaitingConfirmationJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No jobs currently pending customer release</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  When you upload a completion photo for an active job, it will stay here until the customer confirms and releases your 90% net earnings.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {awaitingConfirmationJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const grossValue = acceptedQuote?.totalAmount || job.budgetEstimate || 0;
                  const netPayout = Math.round(grossValue * 0.9);

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-6 border border-purple-200 shadow-xs space-y-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-black text-slate-900">{job.title}</h3>
                            <span className="px-3 py-0.5 rounded-full text-[11px] font-black bg-purple-100 text-purple-900 border border-purple-300">
                              Waiting for Customer Confirmation
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">{job.categoryName} • {job.lga}, {job.state}</p>
                        </div>
                        <div className="sm:text-right">
                          <span className="text-[10px] font-bold uppercase text-purple-600 block">Pending 90% Net Release</span>
                          <span className="text-base font-black text-purple-900 tabular-nums">₦{netPayout.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Live 4-Stage Progress Tracker */}
                      <RealTimeJobStatusTracker job={job} quotes={jobQuotes} compact={true} onRefresh={refreshJobsAndQuotes} />

                      {job.completionNotes && (
                        <div className="p-3.5 bg-purple-50 rounded-2xl border border-purple-200 text-xs text-purple-950">
                          <strong>Your Completion Notes:</strong> "{job.completionNotes}"
                        </div>
                      )}

                      {/* Exact Job Site Location Block */}
                      <div className="p-4 bg-slate-50 border border-slate-200/80 rounded-2xl space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                          <div className="flex items-start gap-2.5">
                            <div className="w-7 h-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                              <MapPin className="w-4 h-4" />
                            </div>
                            <div>
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">
                                Exact Job Site Address
                              </span>
                              <p className="text-xs font-black text-slate-900 leading-snug">
                                {job.address || job.formattedAddress || `${job.cityArea ? job.cityArea + ', ' : ''}${job.lga}, ${job.state}`}
                              </p>
                              <p className="text-[11px] text-slate-500">
                                {job.cityArea ? `${job.cityArea}, ` : ''}{job.lga} LGA, {job.state} State
                              </p>
                            </div>
                          </div>

                          <a
                            href={getGoogleMapsUrl(job)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-2xs shrink-0 transition-colors w-fit"
                          >
                            <Compass className="w-3.5 h-3.5" />
                            <span>Open in Google Maps</span>
                          </a>
                        </div>

                        {(job.locationLatitude && job.locationLongitude || job.locationLandmark) && (
                          <div className="pt-2 border-t border-slate-200/60 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
                            {job.locationLatitude && job.locationLongitude && (
                              <div className="flex items-center gap-1 text-slate-500">
                                <span>GPS Pin:</span>
                                <span className="font-mono text-slate-700 font-semibold">
                                  {job.locationLatitude.toFixed(5)}, {job.locationLongitude.toFixed(5)}
                                </span>
                              </div>
                            )}
                            {job.locationLandmark && (
                              <div className="sm:col-span-2 text-slate-700 bg-amber-50 p-2 rounded-xl border border-amber-200/60">
                                <span className="font-bold text-amber-900">Landmark & Directions:</span>{' '}
                                <span>{job.locationLandmark}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {(() => {
                        const custProf = profiles.find((p) => p.id === job.customerId);
                        const resolvedCustomerPhone =
                          cleanContactPhone(job.customerPhone) ||
                          cleanContactPhone(custProf?.phone) ||
                          cleanContactPhone((job.aiScopeEstimate as any)?.customer_meta?.customerPhone);
                        const resolvedCustomerName =
                          job.customerName && job.customerName !== 'Customer'
                            ? job.customerName
                            : custProf?.fullName || job.customerName || 'Customer';

                        return (
                          <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/60 text-xs text-slate-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <p>
                              Customer: <strong>{resolvedCustomerName}</strong>
                              {resolvedCustomerPhone ? ` (${resolvedCustomerPhone})` : ''} has been notified to inspect and confirm.
                            </p>
                            <div className="flex items-center gap-2">
                              {resolvedCustomerPhone && (
                                <a
                                  href={`tel:${resolvedCustomerPhone}`}
                                  className="px-3.5 py-1.5 rounded-xl bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-1.5"
                                >
                                  <Phone className="w-3.5 h-3.5" />
                                  <span>Call</span>
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => navigateTo('messages', { jobId: job.id, proId: currentUser.id })}
                                className="px-3.5 py-1.5 rounded-xl bg-slate-900 text-white font-bold text-xs flex items-center justify-center gap-1.5 cursor-pointer"
                              >
                                <MessageSquare className="w-3.5 h-3.5" />
                                <span>Message Customer</span>
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                        <button
                          type="button"
                          onClick={() => setSelectedJobForDetails(job)}
                          className="px-3.5 py-2 rounded-xl bg-slate-100 text-slate-700 font-bold text-xs cursor-pointer"
                        >
                          View Full Details
                        </button>
                        <button
                          type="button"
                          onClick={() => setCompletionModalJob(job)}
                          className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer flex items-center gap-1.5 shadow-xs transition-all hover:scale-[1.02]"
                          title="Update Completion Photo"
                        >
                          <Camera className="w-3.5 h-3.5 text-white" />
                          <span>Update Completion Photo</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 5: COMPLETED JOBS */}
        {activeTab === 'completed_jobs' && (
          <div className="space-y-4 animate-fade-in">
            <div className="bg-white p-4 sm:p-5 rounded-3xl border border-slate-200/80 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <h2 className="text-base sm:text-lg font-black text-slate-900">Successfully Completed & Settled Jobs</h2>
                <p className="text-xs text-slate-500">
                  Archive of all jobs confirmed by customers with 90% net earnings credited to your account.
                </p>
              </div>
              <span className="px-3.5 py-1.5 rounded-2xl bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs font-extrabold w-fit">
                {completedJobs.length} Completed
              </span>
            </div>

            {completedJobs.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-3">
                <CheckCircle2 className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No completed jobs yet</h3>
                <p className="text-xs text-slate-500 max-w-sm mx-auto">
                  Completed and confirmed jobs will be archived here along with your financial receipts.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {completedJobs.map((job) => {
                  const jobQuotes = quotes.filter((q) => q.jobId === job.id);
                  const acceptedQuote = jobQuotes.find((q) => q.status === 'accepted');
                  const grossValue = acceptedQuote?.totalAmount || job.budgetEstimate || 0;
                  const netPayout = Math.round(grossValue * 0.9);

                  return (
                    <div
                      key={job.id}
                      className="bg-white rounded-3xl p-6 border border-emerald-200/80 shadow-xs space-y-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="space-y-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-sm sm:text-base font-black text-slate-900">{job.title}</h3>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                              Job Completed
                            </span>
                            <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-800 border border-blue-200">
                              90% Payout Released
                            </span>
                          </div>
                          <p className="text-xs text-slate-500">
                            {job.categoryName} • {job.lga}, {job.state} • Customer: <strong>{job.customerName || 'Verified Customer'}</strong>
                          </p>
                        </div>

                        <div className="sm:text-right">
                          <span className="text-[10px] font-bold uppercase text-slate-400 block">Your 90% Net Earned</span>
                          <span className="text-base font-black text-emerald-700 tabular-nums">+₦{netPayout.toLocaleString()}</span>
                        </div>
                      </div>

                      <RealTimeJobStatusTracker job={job} quotes={jobQuotes} compact={true} onRefresh={refreshJobsAndQuotes} />

                      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-100">
                        <span className="text-xs text-slate-500">
                          Gross Job Value: <strong>₦{grossValue.toLocaleString()}</strong> (10% Platform Fee: ₦{Math.round(grossValue * 0.1).toLocaleString()})
                        </span>
                        <div className="flex items-center gap-2">
                          <a
                            href={getGoogleMapsUrl(job)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs flex items-center gap-1 cursor-pointer transition-colors"
                          >
                            <Compass className="w-3.5 h-3.5" />
                            <span>Maps</span>
                          </a>
                          <button
                            type="button"
                            onClick={() => setSelectedJobForDetails(job)}
                            className="px-3.5 py-1.5 rounded-xl bg-slate-900 text-white font-bold text-xs cursor-pointer hover:bg-slate-800 transition-colors"
                          >
                            Receipt & Details
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* TAB 6: EARNINGS & FINANCIAL LEDGER */}
        {activeTab === 'earnings' && (
          <div className="space-y-6 animate-fade-in">
            {/* Main Financial Summary Component */}
            <EarningsSummaryCard
              professionalId={currentUser.id}
              summary={earningsSummary}
              onRefresh={loadEarnings}
              onRequestPayoutTab={() => setActiveTab('payouts')}
            />

            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs space-y-1">
                <span className="text-xs font-bold text-emerald-600 block">Available Balance</span>
                <p className="text-2xl sm:text-3xl font-black text-slate-900">
                  ₦{earningsSummary.availableEarnings.toLocaleString()}
                </p>
                <p className="text-[11px] text-slate-400">Ready for bank withdrawal</p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs space-y-1">
                <span className="text-xs font-bold text-purple-600 block">Held in Escrow</span>
                <p className="text-2xl sm:text-3xl font-black text-purple-600">
                  ₦{earningsSummary.pendingEarnings.toLocaleString()}
                </p>
                <p className="text-[11px] text-slate-400">Releases upon completion</p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs space-y-1">
                <span className="text-xs font-bold text-slate-600 block">Lifetime Net Earnings</span>
                <p className="text-2xl sm:text-3xl font-black text-slate-900">
                  ₦{earningsSummary.totalProfessionalEarnings.toLocaleString()}
                </p>
                <p className="text-[11px] text-slate-400">Your 90% net earned</p>
              </div>

              <div className="bg-white p-5 rounded-3xl border border-slate-200/80 shadow-xs space-y-1">
                <span className="text-xs font-bold text-amber-600 block">JobMatch Platform Fee</span>
                <p className="text-2xl sm:text-3xl font-black text-amber-600">
                  ₦{earningsSummary.totalJobMatchCommission.toLocaleString()}
                </p>
                <p className="text-[11px] text-slate-400">10% standard marketplace fee</p>
              </div>
            </div>

            {/* Quick Action to Payouts */}
            <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-3xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="space-y-1">
                <h3 className="text-base font-black text-emerald-950">Ready to Withdraw Your Earnings?</h3>
                <p className="text-xs text-emerald-800">
                  Submit a payout request to transfer your available balance into your verified Nigerian bank account.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('payouts')}
                className="px-6 py-3 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md cursor-pointer shrink-0"
              >
                Go to Bank Payouts
              </button>
            </div>

            {/* Recent Transaction Records */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4">
              <h3 className="text-base font-black text-slate-900">Marketplace Transactions & Escrow History</h3>
              {earningsSummary.recentTransactions.length === 0 ? (
                <p className="text-xs text-slate-500">No settled transactions yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-400 font-bold">
                        <th className="pb-3">Reference / Job</th>
                        <th className="pb-3">Gross Value</th>
                        <th className="pb-3">10% Platform Fee</th>
                        <th className="pb-3">Your 90% Net</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {earningsSummary.recentTransactions.map((tx) => (
                        <tr key={tx.id} className="hover:bg-slate-50/60">
                          <td className="py-3 font-semibold text-slate-900">
                            {tx.jobTitle || tx.paymentReference}
                          </td>
                          <td className="py-3 font-mono">₦{tx.grossAmount.toLocaleString()}</td>
                          <td className="py-3 font-mono text-amber-600">-₦{tx.commissionAmount.toLocaleString()}</td>
                          <td className="py-3 font-mono text-emerald-700 font-extrabold">+₦{tx.professionalAmount.toLocaleString()}</td>
                          <td className="py-3">
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                              tx.paymentStatus === 'released'
                                ? 'bg-emerald-50 text-emerald-800'
                                : 'bg-blue-50 text-blue-800'
                            }`}>
                              {tx.paymentStatus === 'released' ? 'Available' : 'In Escrow'}
                            </span>
                          </td>
                          <td className="py-3 text-slate-400">{new Date(tx.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 7: PAYOUTS & BANK TRANSFER */}
        {activeTab === 'payouts' && (
          <div className="space-y-6 animate-fade-in">
            {payoutFeedback && (
              <div
                className={`p-4 rounded-2xl flex items-center justify-between gap-3 text-xs font-bold ${
                  payoutFeedback.type === 'success'
                    ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                    : 'bg-rose-50 text-rose-900 border border-rose-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  {payoutFeedback.type === 'success' ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                  )}
                  <span>{payoutFeedback.text}</span>
                </div>
                <button onClick={() => setPayoutFeedback(null)} className="font-mono text-sm cursor-pointer">✕</button>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left 2 Cols: Verified Bank Account + Withdraw to Bank Form */}
              <div className="lg:col-span-2 space-y-6">
                {/* Verified Bank Recipient Card */}
                <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <h2 className="text-base sm:text-lg font-black text-slate-900 flex items-center gap-2">
                        <Building2 className="w-5 h-5 text-emerald-600" />
                        <span>Verified Nigerian Bank Account</span>
                      </h2>
                      <p className="text-xs text-slate-500">
                        Verified via Paystack Account Resolution & Transfer Recipient API.
                      </p>
                    </div>
                    {verifiedBankAccount?.isVerified && !isEditingBank && (
                      <button
                        type="button"
                        onClick={() => setIsEditingBank(true)}
                        className="px-3.5 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
                      >
                        Change Bank Account
                      </button>
                    )}
                  </div>

                  {verifiedBankAccount?.isVerified && !isEditingBank ? (
                    <div className="p-4 rounded-2xl bg-emerald-50/70 border border-emerald-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="inline-flex items-center gap-1.5 text-[11px] font-black text-emerald-800 uppercase tracking-wider">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span>Paystack Transfer Recipient Active</span>
                        </div>
                        <p className="text-sm font-black text-slate-900">{verifiedBankAccount.accountName}</p>
                        <p className="text-xs text-slate-600 font-medium">
                          {verifiedBankAccount.bankName} • <span className="font-mono font-bold">{verifiedBankAccount.accountNumber}</span>
                        </p>
                      </div>
                      <div className="text-left sm:text-right">
                        <span className="text-[10px] font-bold text-slate-400 uppercase block">Recipient Code</span>
                        <span className="text-xs font-mono font-bold text-emerald-800 bg-white px-2.5 py-1 rounded-lg border border-emerald-200 inline-block">
                          {verifiedBankAccount.recipientCode}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">
                            Select Nigerian Bank
                          </label>
                          <select
                            value={payoutBankCode}
                            onChange={(e) => {
                              const code = e.target.value;
                              setPayoutBankCode(code);
                              setPayoutAccountName('');
                              const matched = nigerianBanks.find((b) => b.code === code);
                              if (matched) setPayoutBankName(matched.name);
                              if (payoutAccountNumber.replace(/\D/g, '').length === 10 && code) {
                                handleAutoResolveBankAccount(code, payoutAccountNumber);
                              }
                            }}
                            className="w-full p-3 rounded-2xl border border-slate-200 text-xs text-slate-800 bg-white focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                          >
                            <option value="">-- Select Your Bank --</option>
                            {nigerianBanks.map((b) => (
                              <option key={`${b.code}-${b.name}`} value={b.code}>
                                {b.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-slate-700 mb-1">
                            10-Digit NUBAN Account Number
                          </label>
                          <input
                            type="text"
                            value={payoutAccountNumber}
                            onChange={(e) => {
                              const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
                              setPayoutAccountNumber(clean);
                              if (clean.length === 10 && payoutBankCode) {
                                handleAutoResolveBankAccount(payoutBankCode, clean);
                              } else {
                                setPayoutAccountName('');
                              }
                            }}
                            placeholder="0123456789"
                            maxLength={10}
                            className="w-full p-3 rounded-2xl border border-slate-200 text-xs font-mono text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                          />
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <label className="block text-xs font-bold text-slate-700">
                            Account Holder Name (Verified by Paystack){' '}
                            {isResolvingBankAccount && (
                              <span className="text-emerald-600 font-bold">(Resolving with Paystack...)</span>
                            )}
                          </label>
                          {payoutBankCode && payoutAccountNumber.replace(/\D/g, '').length === 10 && !isResolvingBankAccount && (
                            <button
                              type="button"
                              onClick={() => handleAutoResolveBankAccount(payoutBankCode, payoutAccountNumber)}
                              className="text-[11px] font-bold text-emerald-700 hover:underline cursor-pointer"
                            >
                              {payoutAccountName ? 'Re-verify Name' : 'Verify Account Name'}
                            </button>
                          )}
                        </div>
                        <input
                          type="text"
                          value={payoutAccountName}
                          readOnly
                          placeholder={
                            isResolvingBankAccount
                              ? 'Resolving account holder name from Paystack...'
                              : 'Select your bank and enter your 10-digit NUBAN to resolve account name'
                          }
                          className={`w-full p-3 rounded-2xl border text-xs font-bold focus:outline-hidden ${
                            payoutAccountName
                              ? 'border-emerald-300 bg-emerald-50/60 text-emerald-950'
                              : 'border-slate-200 bg-slate-50 text-slate-800'
                          }`}
                        />
                        {payoutAccountName && !isResolvingBankAccount && (
                          <p className="mt-1.5 text-[11px] font-bold text-emerald-700 flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                            <span>Confirmed account holder: {payoutAccountName}</span>
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-3">
                        {isEditingBank && (
                          <button
                            type="button"
                            onClick={() => {
                              setIsEditingBank(false);
                              if (verifiedBankAccount) {
                                setPayoutBankCode(verifiedBankAccount.bankCode);
                                setPayoutBankName(verifiedBankAccount.bankName);
                                setPayoutAccountNumber(verifiedBankAccount.accountNumber);
                                setPayoutAccountName(verifiedBankAccount.accountName);
                              }
                            }}
                            className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs cursor-pointer"
                          >
                            Cancel
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleSaveVerifiedBankAccount()}
                          disabled={
                            isSavingBankAccount ||
                            isResolvingBankAccount ||
                            !payoutBankCode ||
                            payoutAccountNumber.replace(/\D/g, '').length !== 10
                          }
                          className="px-5 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs cursor-pointer disabled:opacity-50"
                        >
                          {isSavingBankAccount
                            ? 'Verifying & Creating Recipient...'
                            : payoutAccountName
                            ? `Confirm & Save (${payoutAccountName})`
                            : 'Verify & Save Bank Account'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Withdraw to Bank Form */}
                <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div>
                      <h2 className="text-base sm:text-lg font-black text-slate-900">Withdraw to Bank</h2>
                      <p className="text-xs text-slate-500">
                        Immediate transfer of your released 90% net earnings via Paystack Transfers.
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-slate-400 block font-bold">Available to Withdraw</span>
                      <span className="text-lg font-black text-emerald-600 font-mono">
                        ₦{earningsSummary.availableEarnings.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  <form onSubmit={handleRequestPayout} className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-bold text-slate-700">
                          Withdrawal Amount (₦)
                        </label>
                        {earningsSummary.availableEarnings >= 1000 && (
                          <button
                            type="button"
                            onClick={() => setPayoutAmount(String(earningsSummary.availableEarnings))}
                            className="text-[11px] font-bold text-emerald-600 hover:underline cursor-pointer"
                          >
                            Withdraw Full Available (₦{earningsSummary.availableEarnings.toLocaleString()})
                          </button>
                        )}
                      </div>
                      <input
                        type="number"
                        value={payoutAmount}
                        onChange={(e) => setPayoutAmount(e.target.value)}
                        placeholder={`e.g. ${Math.max(1000, Math.min(earningsSummary.availableEarnings, 20000))}`}
                        max={earningsSummary.availableEarnings}
                        min={1000}
                        className="w-full p-3 rounded-2xl border border-slate-200 text-sm font-mono font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500 focus:outline-hidden"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isRequestingPayout || earningsSummary.availableEarnings < 1000}
                      className="w-full py-3.5 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 min-h-[44px]"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>
                        {isRequestingPayout ? 'Initiating Paystack Transfer...' : 'Withdraw to Bank'}
                      </span>
                    </button>
                  </form>
                </div>
              </div>

              {/* Right Col: Payout Policy & Summary */}
              <div className="space-y-4">
                <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-3">
                  <h3 className="text-sm font-black text-slate-900">Paystack Withdrawal Rules</h3>
                  <ul className="text-xs text-slate-600 space-y-2 list-disc list-inside">
                    <li>Minimum withdrawal amount is ₦1,000 (100,000 kobo).</li>
                    <li>JobMatch NG retains 10% commission at job completion; <strong>0% extra fee</strong> is deducted when you withdraw.</li>
                    <li>Only released, completed earnings can be withdrawn. Active escrow jobs remain locked until customer confirmation.</li>
                    <li>If a bank transfer fails or reverses, your available balance is automatically restored once.</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Payout History Table */}
            <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-black text-slate-900">Withdrawal History</h3>
                <button
                  type="button"
                  onClick={loadEarnings}
                  className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${loadingEarnings ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
              </div>
              {earningsSummary.payoutHistory.length === 0 ? (
                <p className="text-xs text-slate-500">No withdrawal requests submitted yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-slate-100 text-slate-400 font-bold">
                        <th className="pb-3">Reference</th>
                        <th className="pb-3">Amount</th>
                        <th className="pb-3">Bank Details</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Date</th>
                        <th className="pb-3 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 text-slate-700">
                      {earningsSummary.payoutHistory.map((po) => (
                        <tr key={po.id} className="hover:bg-slate-50/60">
                          <td className="py-3 font-mono text-slate-900">{po.payoutReference}</td>
                          <td className="py-3 font-mono font-extrabold text-slate-900">₦{po.amount.toLocaleString()}</td>
                          <td className="py-3">
                            {po.bankName} • {po.accountNumber} ({po.accountName})
                          </td>
                          <td className="py-3">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                po.status === 'paid' || po.status === 'successful'
                                  ? 'bg-emerald-50 text-emerald-800'
                                  : po.status === 'processing' || po.status === 'pending' || po.status === 'approved'
                                  ? 'bg-amber-50 text-amber-800'
                                  : 'bg-rose-50 text-rose-800'
                              }`}
                            >
                              {po.status.toUpperCase()}
                            </span>
                            {po.failureReason && (
                              <span className="block text-[10px] text-rose-600 mt-0.5">{po.failureReason}</span>
                            )}
                          </td>
                          <td className="py-3 text-slate-400">{new Date(po.createdAt).toLocaleDateString()}</td>
                          <td className="py-3 text-right">
                            {(po.status === 'processing' || po.status === 'pending') && po.payoutReference && (
                              <button
                                type="button"
                                onClick={() => handleReconcilePayout(po.payoutReference!)}
                                disabled={reconcilingReference === po.payoutReference}
                                className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-[11px] cursor-pointer disabled:opacity-50"
                              >
                                {reconcilingReference === po.payoutReference ? 'Checking...' : 'Verify Status'}
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 8: PROFILE */}
        {activeTab === 'profile' && (
          <div className="space-y-6 animate-fade-in">
            <ProfessionalProfileEditSection onNavigateToVerification={() => setActiveTab('verification')} />
          </div>
        )}

        {/* TAB 8B: IDENTITY & KYC VERIFICATION */}
        {activeTab === 'verification' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-3xl p-4 sm:p-6 border border-slate-200/80 shadow-xs">
              <KYCVerificationCenter
                onClose={() => setActiveTab('profile')}
                onSuccess={() => {
                  loadEarnings();
                }}
              />
            </div>
          </div>
        )}

        {/* TAB 9: NOTIFICATIONS */}
        {activeTab === 'notifications' && (
          <div className="space-y-6 animate-fade-in">
            {currentUser?.id && (
              <PushNotificationSettingsCard
                userId={currentUser.id}
                userRole="professional"
              />
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-black text-slate-900">Professional Alerts & Updates</h2>
              <span className="text-xs text-slate-500">{proNotifications.length} total</span>
            </div>

            {proNotifications.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 text-center border border-slate-200/80 space-y-2">
                <Bell className="w-8 h-8 text-slate-400 mx-auto" />
                <h3 className="text-base font-black text-slate-900">No notifications</h3>
                <p className="text-xs text-slate-500">You're all caught up with your job alerts and payments.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {proNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    onClick={() => {
                      if (!notif.read) markNotificationAsRead(notif.id);
                    }}
                    className={`p-5 rounded-3xl border transition-all cursor-pointer flex items-start gap-3 ${
                      notif.read ? 'bg-white border-slate-200/80' : 'bg-emerald-50/50 border-emerald-200 shadow-xs'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0 mt-0.5">
                      <Bell className="w-4 h-4" />
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-xs font-black text-slate-900">{notif.title}</h4>
                        <span className="text-[10px] text-slate-400">{new Date(notif.createdAt).toLocaleDateString()}</span>
                      </div>
                      <p className="text-xs text-slate-600">{notif.message}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* TAB 10: HELP & SUPPORT */}
        {activeTab === 'help' && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200/80 shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-base sm:text-lg font-black text-slate-900">Artisan Guide & Support Center</h2>
                  <p className="text-xs text-slate-500">Best practices for winning quotes and fast escrow payouts.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-2">
                  <h4 className="text-xs font-extrabold text-slate-900">How to Win More Customer Quotes</h4>
                  <p className="text-xs text-slate-600">
                    Provide transparent itemized quotes (materials vs labor) and respond within 15 minutes of job posting to maximize your customer acceptance rate.
                  </p>
                </div>

                <div className="p-5 bg-slate-50 rounded-2xl border border-slate-200/60 space-y-2">
                  <h4 className="text-xs font-extrabold text-slate-900">Completion Photo Requirement</h4>
                  <p className="text-xs text-slate-600">
                    Always take a clear, well-lit photo under 200KB when your physical repair is finished. This builds customer confidence and speeds up completion confirmation.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-xs animate-fade-in">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl border border-slate-100 text-center space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <LogOut className="w-6 h-6" />
            </div>
            <h3 className="text-base font-black text-slate-900">Sign Out of Professional Portal?</h3>
            <p className="text-xs text-slate-500">
              You will need to sign back in with your credentials to manage active jobs and view available matches.
            </p>
            <div className="flex items-center gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
                className="flex-1 py-2.5 rounded-xl bg-slate-100 text-slate-700 font-bold text-xs cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex-1 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-700 text-white font-black text-xs cursor-pointer"
              >
                {isLoggingOut ? 'Signing out...' : 'Sign Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Quote Modal */}
      {selectedJobForQuote && (
        <SubmitQuoteModal
          job={selectedJobForQuote}
          isOpen={Boolean(selectedJobForQuote)}
          onClose={() => {
            setSelectedJobForQuote(null);
            setActiveTab('overview');
          }}
        />
      )}

      {/* Completion Photo Upload Modal */}
      {completionModalJob && (
        <CompletionPhotoUploadModal
          job={completionModalJob}
          isOpen={Boolean(completionModalJob)}
          professionalId={currentUser.id}
          onClose={() => setCompletionModalJob(null)}
          onSuccess={async () => {
            if (completionModalJob) {
              completionModalJob.status = 'completed_by_professional';
              completionModalJob.completionStatus = 'completed_by_professional';
              completionModalJob.completed_by_professional = true;
            }
            setCompletionModalJob(null);
            setActiveTab('awaiting_confirmation');
            if (refreshJobsAndQuotes) {
              await refreshJobsAndQuotes();
            }
            loadEarnings();
          }}
        />
      )}

      {/* Full Job Details Modal */}
      {selectedJobForDetails && (
        <JobDetailsModal
          job={selectedJobForDetails}
          isOpen={Boolean(selectedJobForDetails)}
          onClose={() => setSelectedJobForDetails(null)}
        />
      )}
    </div>
  );
};
